/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ModalController } from "../../../model/navigation";
import { InputAction } from "../../../model/enums/InputAction";
import {
  alertOverlayActionSelected,
  dismissAlertOverlay,
  focusNextAction,
  focusPrevAction,
  selectAlertOverlayActions,
  selectFocusedActionIndex
} from "../../../viewModel/alertOverlay";
import type { AlertOverlayAction } from "../../../viewModel/alertOverlay";
import type { UnityAppDispatch, UnityRootState } from "../../../viewModel";

/** Modal name constant for the AlertOverlay modal. */
export const ALERT_OVERLAY_MODAL_NAME: string = "ALERT_OVERLAY";

/**
 * ModalController for the AlertOverlay.
 *
 * D-pad routing:
 *   NAV_LEFT  → focus previous action button
 *   NAV_RIGHT → focus next action button
 *   NAV_OK    → dismiss overlay with the focused action's ID
 *   BACK      → dismiss overlay with the first action's ID
 *
 * All handlers return true to consume the input and prevent it from
 * bubbling to the screen behind the overlay.
 */
export class AlertOverlayController extends ModalController {
  private readonly mDispatch: UnityAppDispatch;
  private readonly mGetState: () => UnityRootState;

  public constructor(
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState
  ) {
    super(ALERT_OVERLAY_MODAL_NAME);
    this.mDispatch = dispatch;
    this.mGetState = getState;
  }

  protected initialize(): void {
    this.registerHandler(InputAction.NAV_LEFT, (): boolean => {
      this.mDispatch(focusPrevAction());
      return true;
    });

    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => {
      this.mDispatch(focusNextAction());
      return true;
    });

    this.registerHandler(InputAction.NAV_OK, (): boolean => {
      const state = this.mGetState();
      const actions = selectAlertOverlayActions(state);
      const focusedIndex = selectFocusedActionIndex(state);
      const action = actions[focusedIndex];
      if (action !== undefined) {
        this.selectAction(action);
      }
      return true;
    });

    this.registerHandler(InputAction.BACK, (): boolean => {
      const state = this.mGetState();
      const actions = selectAlertOverlayActions(state);
      if (actions.length > 0) {
        this.selectAction(actions[0]);
      }
      return true;
    });

    // Consume all remaining navigation keys so they don't reach the screen
    this.registerHandler(InputAction.NAV_UP, (): boolean => true);
    this.registerHandler(InputAction.NAV_DOWN, (): boolean => true);

    // Swallow the matching keyup for every action consumed above. A screen
    // behind the overlay that registers its own keyup handler (FullScreenPlayback
    // uses NAV_LEFT/NAV_RIGHT keyup for skip/hold-release) would otherwise act
    // on the release of a key whose keydown this overlay already consumed —
    // the TSRI recovery overlay can surface over the player. Same reasoning,
    // and the same set, as {@link UnityDialogController}.
    this.registerHandler(InputAction.NAV_LEFT, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_UP, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_DOWN, (): boolean => true, "keyup");
    this.registerHandler(InputAction.NAV_OK, (): boolean => true, "keyup");
    this.registerHandler(InputAction.BACK, (): boolean => true, "keyup");
  }

  /**
   * A keeps-open action (Retry) records the selection without dismissing, so
   * its loading state stays on screen; every other action dismisses.
   */
  private selectAction(action: AlertOverlayAction): void {
    if (action.keepsOverlayOpen === true) {
      this.mDispatch(alertOverlayActionSelected(action.id));
      return;
    }
    this.mDispatch(dismissAlertOverlay(action.id));
  }
}
